import { Location } from '@angular/common';
import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FamilyCodes } from 'src/app/core/_enums/family.code';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Combustible } from 'src/app/core/_models/combustible';
import { CustomIndustryRequest } from 'src/app/core/_models/customIndustryRequest';
import { EnergyProfile, EnergyProfileCombustible } from 'src/app/core/_models/energyProfile';
import { Family } from 'src/app/core/_models/family';
import { Industrie } from 'src/app/core/_models/industrie';
import { MakeModel } from 'src/app/core/_models/makeModel';
import { UserEntity } from 'src/app/core/_models/userEntity';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CombustibleService } from 'src/app/core/_services/combustible.service';
import { IndustrieService } from 'src/app/core/_services/industrie.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { requiredIfCondition } from 'src/app/core/_utils/validation';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { DialogAddCompositionComponent } from '../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogEditCombustibleFormulaComponent } from '../shared/dialog-edit-combustible-formula/dialog-edit-combustible-formula.component';
import { DialogInfoComponent } from '../shared/dialog-info/dialog-info.component';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { DialogSelectEuShareComponent } from '../shared/dialog-select-eu-share/dialog-select-eu-share.component';

@Component({
  selector: 'app-industrie',
  templateUrl: './industrie.component.html',
  styleUrls: ['./industrie.component.scss']
})
export class IndustrieComponent implements OnInit, DoCheck {
  currentItem: Industrie = {};
  form!: FormGroup;
  families: Array<Family> = [];
  makeModels: Array<MakeModel> = [];
  perimeterId!: string;
  isCoating: boolean = false;
  isFormulation: boolean = false;
  favoriteId?: string;
  manageIndustries!: boolean;
  acuView!: boolean;
  canCreateCustomIndustry!: boolean;
  canValidateCustomIndustry!: boolean;

  plantType: 'default' | 'custom' = 'default';
  xlsFile: File | null = null;
  pdfFile: File | null = null;
  xlsFileName = '';
  pdfFileName = '';
  // true while the shown file is the one already on the server (no local File yet) → download icon instead
  // of check, and it is fetched + re-sent on submit if the OTHER file is the one being changed.
  xlsFromServer = false;
  pdfFromServer = false;
  plantStatus: 'validated' | 'pending' = 'validated';
  savedPlantIsCustom = false;
  editingPlant = false;
  customizing = false;
  uploadsError = false;
  // specific backend validation message (invalid Excel/PDF), shown inline under the upload zones
  fileError = '';
  energyProfile: EnergyProfile | null = null;
  // the Pending request, when there is one — its proposed base info feeds "Modifications demandées"
  pendingRequest: CustomIndustryRequest | null = null;

  //Combustibles
  @ViewChild(MatTable) tableCombustibles!: MatTable<any>;
  @ViewChild('xlsFileInput') xlsFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('pdfFileInput') pdfFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('combustibleTotalRow') combustibleTotalRowRef?: ElementRef<HTMLElement>;
  displayedColumns: string[] = ['code', 'displayName', 'value', 'delete'];

  constructor(
    private industrieService: IndustrieService,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private location: Location,
    private combustibleService: CombustibleService,
    private utilsService: UtilsService,
    private spinnerService: SpinnerOverlayService,
    private authService: AuthService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService
  ) {
    this.form = this.formBuilder.group({
      resourceId: [],
      code: ['', Validators.required],
      displayName: ['', Validators.required],
      owner: [{ value: '', disabled: true }],
      creator: [{ value: '', disabled: true }],
      ownerId: [],
      address_L3: ['', Validators.required],
      address_L4: [''],
      address_L5: [''],
      address_Location: ['', Validators.required],
      address_PC: [null, Validators.required],
      combustibles: [],
      familyId: ['', Validators.required],
      mobileStation: [],
      // makeModelId: [],
      status: [],
      ceu: this.formBuilder.group({
        en: [186, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_energy')],
        co: [0.00104, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_co2')],
        gn: [0, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_natural_aggregates')],
        ae: [0, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_asphalt_aggregates')],
        mr: [0, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_recycled_materials')],
        drc: [0, (control: AbstractControl) => requiredIfCondition(!this.isFormulation, control, 'product_field_reused_earthwork')]
      })
    });
  }

  ngOnInit(): void {
    this.industrieService.getIndustriesFamily().subscribe((f: any) => {
      if (f.length > 0) {
        this.families = f;
      }
    });

    // ONE subscription — the previous nested params.subscribe stacked a new inner subscription on every
    // id change (component is reused across :id), so switching plants fired loadIndustrie N times and
    // overlapping loads made the page flicker.
    this.activeRoute.params.subscribe((params) => {
      this.perimeterId = this.authService.roleSubject.value.perimeterId;
      const resourceId = params['id'];
      if (resourceId) {
        this.loadIndustrie(resourceId);
      } else {
        this.editingPlant = false;
        this.customizing = false;
        this.uploadsError = false;
        this.savedPlantIsCustom = false;
        this.plantStatus = 'validated';
        this.form.patchValue({});
      }
    });
  }

  // Loads the industry and (re)seeds the form from it. The form therefore always holds the values the plant
  // CURRENTLY RUNS ON: while a request is pending the backend still serves the last approved ones, and the
  // requested changes are shown apart, in the pending banner (see pendingChanges).
  private loadIndustrie(resourceId: string): void {
    this.editingPlant = false;
    this.customizing = false;
    this.uploadsError = false;
    this.savedPlantIsCustom = false;
    this.plantStatus = 'validated';
    this.pendingRequest = null;

    this.industrieService.get(resourceId).subscribe((industrie) => {
      this.form.patchValue(industrie);
      this.currentItem = industrie;
      if (this.currentItem && this.currentItem.resourceId) {
        this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
          this.favoriteId = res ? res.userFavoriteId : '';
        });
      }
      this.checkIsCoating(this.currentItem.familyId);
      // isCustom only means "has an Approved override" (backend keeps the previous data live
      // while a request is Pending) — but the pending banner below is itself gated on
      // savedPlantIsCustom, so it must also be true while pending, not just once approved.
      this.savedPlantIsCustom = industrie.customStatus === 'pending' || industrie.customStatus === 'validated';
      this.plantStatus = industrie.customStatus === 'pending' ? 'pending' : 'validated';
      if (this.hasEnergyData && this.currentItem.resourceId) {
        this.industrieService.getEnergyProfile(this.currentItem.resourceId).subscribe((profile) => {
          this.energyProfile = profile;
        });
      }
      // the pending request carries the proposed base info -> "Modifications demandées" in the banner
      if (this.plantStatus === 'pending' && industrie.pendingCustomIndustryValuesId) {
        this.industrieService.getCustomIndustryRequest(industrie.pendingCustomIndustryValuesId).subscribe((request) => {
          this.pendingRequest = request;
        });
      }
      // this.getMakeModels(this.currentItem.familyId)
      this.form.controls.familyId.disable();
    });
  }

  ngDoCheck(): void {
    if (this.authService?.accessesValues) this.manageIndustries = this.authService.accessesValues.includes(FunctionalityCode.ManageIndustry);

    this.acuView = this.authService.accessesValues.includes(FunctionalityCode.ACUView);
    // Ouvert temporairement à toutes les régions pour recetter la V2 en préprod.
    const isFrance = true;
    this.canCreateCustomIndustry = isFrance && this.authService.accessesValues.includes(FunctionalityCode.CreateCustomIndustry);
    this.canValidateCustomIndustry = isFrance && this.authService.accessesValues.includes(FunctionalityCode.ValidateCustomIndustry);
  }

  get formCrls() {
    return this.form.controls;
  }

  get isDisabled() {
    return this.activeRoute.snapshot.params['id'];
  }

  get isSaved(): boolean {
    return !!this.currentItem?.resourceId;
  }

  // Excel+PDF are mandatory when this save MUST create a request: a brand-new custom plant, or customizing
  // a default one. Re-modifying an existing custom plant is handled separately (option 1): a request is
  // created only if a new Excel is attached, so editing does not force re-uploading the files.
  get requiresValidation(): boolean {
    return (!this.isSaved && this.plantType === 'custom' && this.isCoating) || this.customizing;
  }

  // show the Excel/PDF deposit zones + info banner ONLY while creating/customizing/editing a request
  // (not when simply viewing a validated custom plant)
  get showUploads(): boolean {
    return (!this.isSaved && this.plantType === 'custom' && this.isCoating) || this.customizing || this.editingPlant;
  }

  // fields read-only: pending plant, or saved custom validated plant not yet unlocked via "Modifier"
  get plantLocked(): boolean {
    if (!this.isSaved) return false;
    if (this.plantStatus === 'pending') return true;
    if (this.savedPlantIsCustom && !this.editingPlant) return true;
    return false;
  }

  get formDisabled(): boolean {
    const rightsLocked = !(this.manageIndustries || !(this.currentItem?.canUpdate == true || !this.currentItem?.resourceId));
    return rightsLocked || this.plantLocked;
  }

  // thermal data is only available for a custom plant that has been validated
  get isPlantValidated(): boolean {
    return this.savedPlantIsCustom && this.plantStatus === 'validated';
  }

  // The energy card shows the values the plant CURRENTLY RUNS ON — the last approved ones. A new pending
  // request does not replace them (the backend keeps serving the approved row until the ASTPU decides), so
  // the card must stay visible while a request is pending. Only a plant that has never been validated
  // (born-custom awaiting its first decision, or a plain default plant) has nothing to show.
  get hasEnergyData(): boolean {
    // fall back to the plant's own status when hasValidatedValues is absent (older API): never show LESS
    // than before — a validated custom plant always had its energy card.
    return !!this.currentItem?.hasValidatedValues || this.isPlantValidated;
  }

  // Les justificatifs de l'usine restent servis par le serveur tant qu'une nouvelle demande ne les a pas
  // remplacés : leur nom vient du profil énergétique, donc de la demande approuvée.
  get hasCustomFiles(): boolean {
    return !!(this.energyProfile?.excelName || this.energyProfile?.pdfName);
  }

  // The base-info changes carried by the pending request, "actuel → demandé", for the banner. Only the
  // fields that actually change are listed (a request may carry only new files). Mirrors the validator
  // page's baseDiff, but keeps just the modified rows since the current values are already in the form.
  get pendingChanges(): { labelKey: string; before: string; after: string }[] {
    const req = this.pendingRequest;
    if (!req) return [];
    const cur = req.current;
    const fmt = (v: any) => (v == null || v === '' ? '—' : String(v));
    const fmtBool = (v: any) => (v == null ? '—' : this.translate.instant(v ? 'dialog_overview_btn_yes' : 'dialog_overview_btn_no'));

    const rows: { labelKey: string; before: string; after: string }[] = [];
    const add = (labelKey: string, before: any, after: any, isBool = false) => {
      const b = isBool ? fmtBool(before) : fmt(before);
      const a = isBool ? fmtBool(after) : fmt(after);
      if (b !== a) rows.push({ labelKey, before: b, after: a });
    };

    add('common_field_label', cur?.displayName, req.displayName);
    add('common_field_code', cur?.code, req.code);
    add('ref_field_family', cur?.familyDisplayName, req.familyDisplayName);
    add('agency_field_address_line1', cur?.addressL3, req.addressL3);
    add('agency_field_address_line2', cur?.addressL4, req.addressL4);
    add('agency_field_address_line3', cur?.addressL5, req.addressL5);
    add('agency_field_postal_code', cur?.addressPC, req.addressPC);
    add('agency_field_city', cur?.addressLocation, req.addressLocation);
    add('industrie_checkbox_mobile_station', cur?.mobileStation, req.mobileStation, true);
    return rows;
  }

  // Files replaced by the pending request (excelChanged/pdfChanged are undefined for a creation, where
  // every file is new by definition and there is nothing to signal).
  get pendingFileChanges(): string[] {
    const req = this.pendingRequest;
    if (!req) return [];
    const labels: string[] = [];
    if (req.excelChanged) labels.push(this.translate.instant('industrie_fdes_pending_file_excel'));
    if (req.pdfChanged) labels.push(this.translate.instant('industrie_fdes_pending_file_pdf'));
    return labels;
  }

  // app-fdes-thermal-card takes flat display values — map the energy-profile response onto them
  get thermalConso(): string {
    return this.energyProfile?.consoTotal != null ? String(this.energyProfile.consoTotal) : '';
  }

  // Combustibles utilisés du mix — une ligne par combustible dans la carte (nom, part, FE).
  get thermalCombustibles(): EnergyProfileCombustible[] {
    return (this.energyProfile?.combustibles || []).filter((c) => (c.usagePct ?? 0) > 0);
  }

  // true whenever the plant is (or is becoming) a custom one — its combustible mix then
  // comes from the extracted Excel / energy card, not the manual combustible table
  get isCustomPlant(): boolean {
    return this.savedPlantIsCustom || (!this.isSaved && this.plantType === 'custom') || this.customizing;
  }

  get hasCombustibles(): boolean {
    return this.formCrls?.combustibles?.value?.length > 0;
  }

  get combustibleTotal(): number {
    const list = this.formCrls?.combustibles?.value || [];
    return list.reduce((sum: number, c: any) => sum + (Number(c.value) || 0), 0);
  }

  // The "sum = 100%" rule only bites once a mix has actually been ENTERED. A plant that never had a formula
  // has no mix to derive: UsagePercentage stays NULL and the table reads 0 on every line. Those plants had
  // no mix column at all before this feature, so enforcing the rule on them locks a page that used to save
  // fine — 459 plants in preprod, most of them real.
  // Same rule as the server (CombustibleMix.IsSet + IndustryValidator): neither side may accept what the
  // other refuses. No exemption for a custom plant — its Excel mix is rounded so it sums to exactly 100
  // (RoundPercentagesTo100), so a mix that is set but off-100 has no way of being written in the first place.
  get combustibleValid(): boolean {
    if (!this.isCoating || !this.hasCombustibles) return true;
    if (this.combustibleTotal === 0) return true;
    return this.combustibleTotal === 100;
  }

  setValue(event: any, inputFor: string) {
    this.formCrls.ceu.get(inputFor)?.setValue(event.value);
  }

  openDialogDelete(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_industrie') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.delete(resourceId);
      }
    });
  }

  delete(resourceId: string) {
    if (resourceId) {
      this.industrieService.delete(resourceId).subscribe({
        next: () => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuIndustry]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  onShare(resourceId: string) {
    var eusToShare: UserEntity[] = [];

    this.industrieService.getEUsToShareIndustry(resourceId).subscribe((data) => {
      const dialogRef = this.dialog.open(DialogSelectEuShareComponent, {
        data: data
      });

      dialogRef.afterClosed().subscribe((dataResult) => {
        if (dataResult) {
          dataResult.forEach((el: any) => {
            eusToShare?.push({
              displayName: el?.displayName,
              euId: el?.resourceId
            });
          });
          this.industrieService.shareIndustry(resourceId, eusToShare).subscribe(
            () => {
              this.snackbarService.open(SnackbarTypes.share);
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          );
        }
      });
    });
  }

  async clickSave(industrie: Industrie) {
    // read-only plant (pending request, or validated custom plant not unlocked via "Modifier"): the Save
    // button is hidden, this guards the remaining entry points (keyboard, stale view).
    if (this.plantLocked) return;

    this.uploadsError = false;
    this.fileError = '';

    // FRONT validation: combustible usage must total 100%
    if (this.isCoating && this.hasCombustibles && !this.combustibleValid) {
      // le message vit en bas de la carte combustible, hors écran depuis le bouton Enregistrer :
      // sans ça le clic ne produit rien de visible (même logique que la page formule).
      this.scrollToCombustibleTotal();
      return;
    }

    // a custom plant request needs the Excel + PDF
    if (this.requiresValidation && (!this.xlsFile || !this.pdfFile)) {
      this.uploadsError = true;
      return;
    }

    if (this.form.invalid) {
      // otherwise the click looks like a no-op: surface the field errors (same as the formula page)
      this.form.markAllAsTouched();
      return;
    }

    // ANY change to an EXISTING plant that is (or becomes) custom goes through validation: re-modifying a
    // custom plant (editingPlant) OR customizing a default one (customizing). We create a request carrying
    // the new base info (name/address/family/…) + files (a kept file is re-fetched on submit); the industry
    // is NOT updated directly — the new values are applied only on approval (old values stay live until then).
    if (this.isSaved && (this.editingPlant || this.customizing)) {
      if (!this.xlsFileName || !this.pdfFileName) {
        this.uploadsError = true;
        return;
      }
      // Re-modifying a custom plant with nothing actually modified would create a request the validator
      // could only rubber-stamp (no diff, no new file). Say so instead of submitting it. Not applicable to
      // `customizing`: turning a default plant custom IS the change, whatever the base info says.
      if (this.editingPlant && !this.hasChangesToSubmit()) {
        const info = this.dialog.open(DialogInfoComponent, {
          data: {
            title: this.translate.instant('industrie_fdes_no_change_title'),
            message: this.translate.instant('industrie_fdes_no_change')
          }
        });
        // Ok = the user acknowledges there is nothing to submit -> leave edit mode (reload = form back to
        // the values in use + file state reset, exactly what startEdit() opened). A dismissal (backdrop
        // click / ESC) is NOT an acknowledgement: keep the edit open so nothing typed is thrown away.
        info.afterClosed().subscribe((acknowledged) => {
          if (!acknowledged) return;
          const id = this.currentItem?.resourceId;
          if (id) this.loadIndustrie(id);
          else this.editingPlant = false;
        });
        return;
      }
      const industryId = this.currentItem?.resourceId;
      if (!industryId) return;
      const ref = this.dialog.open(DialogOverviewComponent, {
        data: { title: this.translate.instant('industrie_fdes_confirm_update_request') }
      });
      ref.afterClosed().subscribe((ok) => {
        if (!ok) return;
        this.createRequestWithFiles(industryId, this.buildBaseInfo(), () => {
          this.toPending();
          // La demande fait passer l'usine en « en attente » : rafraîchir l'arborescence pour y voir
          // apparaître la catégorie « En attente de validation » sans recharger la page. majStore n'est
          // pas utilisé ici (son garde saute le refresh pour un type `update`).
          this.resourceListRefreshService.triggerRefresh();
        });
      });
      return;
    }

    // capture BEFORE persistIndustrie runs — persistIndustrie sets currentItem.resourceId on success,
    // which flips the isSaved getter.
    const needsValidation = this.requiresValidation;
    const wasAlreadySaved = this.isSaved;
    const snackbarType = wasAlreadySaved ? SnackbarTypes.update : SnackbarTypes.add;

    if (needsValidation) {
      if (!this.xlsFile || !this.pdfFile) {
        this.uploadsError = true;
        return;
      }
      // Creation: validate the files FIRST, then create the industry (born-custom) + its request (carrying
      // the base info). Nothing is created when a file is invalid.
      this.industrieService.validateCustomFile(this.xlsFile, this.pdfFile).subscribe({
        next: () =>
          this.persistIndustrie(industrie, (industryId) =>
            this.createRequestWithFiles(
              industryId,
              this.buildBaseInfo(),
              () => {
                this.toPending();
                this.majStore(snackbarType);
              },
              () => this.rollbackNewIndustry(industryId)
            )
          ),
        // invalid Excel/PDF -> show the precise backend message inline under the upload zones
        error: (err) => (this.fileError = this.extractApiError(err))
      });
      return;
    }

    this.persistIndustrie(industrie);
  }

  // collect the front-editable base info to carry on the request (applied to the industry on approval)
  private buildBaseInfo() {
    const v = this.form.getRawValue();
    return {
      code: v.code,
      displayName: v.displayName,
      familyId: v.familyId,
      addressL3: v.address_L3,
      addressL4: v.address_L4,
      addressL5: v.address_L5,
      addressPC: v.address_PC,
      addressLocation: v.address_Location,
      mobileStation: v.mobileStation
    };
  }

  /// Would the request carry anything? A file counts only when the user picked a NEW one: a kept file has
  /// no File object (it is re-fetched from the server on submit), and re-picking the current file is
  /// refused upstream (acceptFileIfDifferent), so xlsFile/pdfFile being set really does mean "replaced".
  /// The form always holds the values in USE (see loadIndustrie), so comparing it with currentItem is the
  /// same diff the validator would see.
  private hasChangesToSubmit(): boolean {
    if (this.xlsFile || this.pdfFile) return true;

    const v = this.form.getRawValue();
    const cur = this.currentItem;
    const same = (a: any, b: any) => (a ?? '') === (b ?? '') || String(a ?? '') === String(b ?? '');

    return !(
      same(v.code, cur?.code) &&
      same(v.displayName, cur?.displayName) &&
      same(v.familyId, cur?.familyId) &&
      same(v.address_L3, cur?.address_L3) &&
      same(v.address_L4, cur?.address_L4) &&
      same(v.address_L5, cur?.address_L5) &&
      same(v.address_PC, cur?.address_PC) &&
      same(v.address_Location, cur?.address_Location) &&
      same(!!v.mobileStation, !!cur?.mobileStation)
    );
  }

  // submits the custom-industry request (Excel + PDF) once the industry itself has been saved and
  // its resourceId is known, then flips the local plant state. Refreshes the store itself (rather
  // than relying on persistIndustrie's own majStore call) — persistIndustrie's refresh runs BEFORE
  // this request exists, so the sidebar/nav tree would otherwise cache a snapshot that's missing
  // the pending state entirely (bug found: industry showed correctly on its own page — toPending()
  // sets those flags directly — but never appeared as custom/pending anywhere driven by the store).
  // resolves both files (kept ones re-fetched from the server so each file can be changed independently),
  // creates the request carrying the proposed base info, and runs onSuccess ONLY if the backend accepts it
  // (valid Excel/PDF). On any error the backend message is surfaced and onSuccess never runs.
  private createRequestWithFiles(industryId: string, baseInfo: any, onSuccess: () => void, onFailure?: () => void): void {
    forkJoin({
      excel: this.fileOrServer(industryId, 'excel', this.xlsFile, this.xlsFileName),
      pdf: this.fileOrServer(industryId, 'pdf', this.pdfFile, this.pdfFileName)
    }).subscribe({
      next: ({ excel, pdf }) => {
        if (!excel || !pdf) {
          if (onFailure) onFailure();
          return;
        }
        this.industrieService.createCustomIndustryRequest(industryId, excel, pdf, baseInfo).subscribe({
          next: () => onSuccess(),
          // invalid Excel/PDF -> show the precise backend message inline under the upload zones
          error: (err) => {
            this.fileError = this.extractApiError(err);
            if (onFailure) onFailure();
          }
        });
      },
      error: () => {
        this.snackbarService.open(this.translate.instant('snackbar_error'));
        if (onFailure) onFailure();
      }
    });
  }

  // A born-custom creation is two calls (create the plant, then its request). If the request fails, the plant
  // would linger as a custom-looking industry with no pending request — invisible to the validation queue.
  // So we delete it: the creation stays all-or-nothing. The form keeps the inputs for an immediate retry.
  private rollbackNewIndustry(industryId: string): void {
    this.industrieService.delete(industryId).subscribe({ next: () => this.majStore(SnackbarTypes.delete) });
    this.currentItem = { ...this.currentItem, resourceId: undefined };
    this.formCrls.resourceId.setValue(null);
    this.savedPlantIsCustom = false;
    this.plantStatus = 'validated';
    this.location.replaceState(SiteMapUrl.MenuIndustry);
  }

  // the freshly-picked File, or the current server file re-downloaded as a File (kept unchanged)
  private fileOrServer(industryId: string, kind: 'excel' | 'pdf', current: File | null, name: string): Observable<File> {
    if (current) return of(current);
    return this.industrieService.getCustomFile(industryId, kind).pipe(map((blob: Blob) => new File([blob], name, { type: blob.type })));
  }

  // extracts a human message from an API error. The ErrorIntercept already rethrows a clean string (the
  // backend message); this also handles a raw HttpErrorResponse defensively.
  private extractApiError(err: any): string {
    if (typeof err === 'string' && err.trim()) return err;
    const body = err?.error;
    if (Array.isArray(body)) {
      const msg = body
        .map((e: any) => e?.errorMessage || e?.ErrorMessage)
        .filter(Boolean)
        .join(' ');
      if (msg) return msg;
    }
    if (typeof body === 'string' && body) return body;
    return this.translate.instant('snackbar_error');
  }

  private toPending(): void {
    this.savedPlantIsCustom = true;
    this.plantStatus = 'pending';
    this.editingPlant = false;
    this.customizing = false;
    this.xlsFile = null;
    this.pdfFile = null;
    this.xlsFileName = '';
    this.pdfFileName = '';
    this.xlsFromServer = false;
    this.pdfFromServer = false;

    // The request is submitted: the plant still RUNS on its previous values, so the form must go back to
    // showing them (it currently holds what the user just typed, which is only *proposed*). Reloading also
    // picks up the new pending state + request id, which feed the "Modifications demandées" banner.
    // Without this the page showed the proposed values until a reload, then silently swapped to the live
    // ones — the same field reading differently before and after F5.
    const industryId = this.currentItem?.resourceId;
    if (industryId) this.loadIndustrie(industryId);
  }

  private persistIndustrie(industrie: Industrie, onSaved?: (industryId: string) => void) {
    if (this.form.invalid) {
      return;
    }

    industrie.code = this.form.getRawValue().code;
    industrie.displayName = this.form.getRawValue().displayName;
    industrie.familyId = this.form.getRawValue().familyId;
    industrie.ruade = this.currentItem.ruade;
    industrie.address_L3 = this.form.getRawValue().address_L3;
    industrie.address_L4 = this.form.getRawValue().address_L4;
    industrie.address_L5 = this.form.getRawValue().address_L5;
    industrie.address_PC = this.form.getRawValue().address_PC;
    industrie.address_Location = this.form.getRawValue().address_Location;
    // rank = ordre d'affichage (position) du combustible dans le catalogue, renvoyé au back comme avant ;
    // value porte le % d'usage du mix (→ UsagePercentage côté back).
    industrie.combustibles = (this.form.getRawValue().combustibles || []).map((c: any, i: number) => ({
      ...c,
      rank: i
    }));

    // INSERT
    if (!this.formCrls?.resourceId?.value) {
      const localCombustibles = this.form.getRawValue().combustibles;
      // Une usine créée par le parcours « personnalisée » naît custom : sans ce marqueur le back ne pose
      // aucune ligne CustomIndustryMode, l'usine reste « par défaut » et redevient sélectionnable dans les
      // formules alors qu'elle n'a aucune valeur validée. Posé à la création seule (personnaliser une usine
      // existante passe par la demande, qui bascule le mode à la validation). Toujours réécrit : après
      // un rollback, currentItem garde le type de la tentative annulée.
      industrie.industryType = this.plantType === 'custom' && this.isCoating ? 'CustomFromCreation' : 'Default';
      delete industrie.resourceId;
      let rep = this.industrieService.add(industrie);
      rep.subscribe(
        (data: Industrie) => {
          this.location.replaceState(`${SiteMapUrl.MenuIndustry}/${data.resourceId}`);
          this.form.patchValue(data);
          // keep the locally-entered combustibles + their usage % (the back returns them without the % and as a new array, which made the table show 0% and duplicate)
          this.formCrls.combustibles.setValue(localCombustibles);
          this.currentItem = data;
          this.currentItem.combustibles = localCombustibles;
          if (this.tableCombustibles) this.tableCombustibles.renderRows();
          this.majStore(SnackbarTypes.add);
          // le parcours « personnalisée » enchaîne sur la demande (createRequestWithFiles) : naviguer ici
          // remonterait le composant et couperait cet enchaînement, rollback compris.
          if (onSaved && data.resourceId) onSaved(data.resourceId);
          else this.router.navigate([`${SiteMapUrl.MenuIndustry}`, data.resourceId]);
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
        }
      );
    }
    // UPDATE
    else {
      let rep = this.industrieService.update(industrie);
      rep.subscribe(
        () => {
          if (this.tableCombustibles) this.tableCombustibles.renderRows();
          this.majStore(SnackbarTypes.update);
          if (onSaved) onSaved(this.formCrls.resourceId.value);
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
        }
      );
    }
  }

  deleteCombustible(combustibles: Combustible[], index: number) {
    combustibles.splice(index, 1);
    this.tableCombustibles.renderRows();
  }

  // Ajouter un combustible dans la liste
  addCombustible() {
    this.combustibleService.getAll().subscribe((data) => {
      this.combustibleService.getAll(GroupBy.favoriteDialog).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((dataResult) => {
          if (!this.formCrls?.combustibles?.value) {
            this.formCrls?.combustibles?.setValue([]);
          }

          if (dataResult) {
            dataResult.forEach((el: any) => {
              this.formCrls?.combustibles?.value.push({
                combustibleCode: el?.code,
                combustibleId: el?.resourceId,
                combustibleName: el?.displayName,
                value: 0
              });
            });

            if (this?.tableCombustibles) this.tableCombustibles.renderRows();
          }
        });
      });
    });
  }

  onDuplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 10, duplicate: (code: string) => this.industrieService.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  // getMakeModels(familyId?: string) {
  //   this.industrieService.getMakeModelsForFamily(familyId).subscribe(rep => {
  //     this.makeModels = rep;
  //     this.isCoating = this.checkIsCoating(familyId);
  //     if (rep.length > 0) {
  //       this.form.patchValue({
  //         makeModelId: this.makeModels[0].makeModelId
  //       })
  //       if (!this.isCoating) {
  //         this.form.patchValue({
  //           combustibles: []
  //         });
  //       }
  //     }
  //   });
  // }

  majStore(snackbarMsgType: string) {
    this.industrieService.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  checkIsCoating(familyId?: string) {
    var selectedFamily = this.families.find((f) => f.familyId == familyId);
    this.isCoating = selectedFamily?.code == FamilyCodes.CoatedIndustry;

    this.isFormulation = selectedFamily?.ceModel != undefined;

    if (selectedFamily?.code == FamilyCodes.BitumeModifyIndustry) {
      this.formCrls.ceu.get('en')?.setValue(186);
      this.formCrls.ceu.get('co')?.setValue(0.00104);
    }

    if (selectedFamily?.code == FamilyCodes.EmulsionIndustry) {
      this.formCrls.ceu.get('en')?.setValue(331.09);
      this.formCrls.ceu.get('co')?.setValue(0.00897);
    }
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }

  // Clicking a zone always opens the picker (to replace). Resetting the native input value first ensures
  // re-picking the SAME file still fires (change); the current selection is kept until a new file is chosen
  // (cancel keeps it).
  attachXls() {
    if (this.plantLocked) return;
    this.xlsFileInputRef.nativeElement.value = '';
    this.xlsFileInputRef.nativeElement.click();
  }

  attachPdf() {
    if (this.plantLocked) return;
    this.pdfFileInputRef.nativeElement.value = '';
    this.pdfFileInputRef.nativeElement.click();
  }

  onXlsFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      (event.target as HTMLInputElement).value = '';
      return;
    }
    this.acceptFileIfDifferent('excel', file, () => {
      this.xlsFile = file;
      this.xlsFileName = file.name;
      this.xlsFromServer = false;
      this.fileError = '';
    });
  }

  onPdfFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      (event.target as HTMLInputElement).value = '';
      return;
    }
    this.acceptFileIfDifferent('pdf', file, () => {
      this.pdfFile = file;
      this.pdfFileName = file.name;
      this.pdfFromServer = false;
      this.fileError = '';
    });
  }

  /// Attaching the file the plant ALREADY runs on is a no-op request: the backend compares content, so the
  /// request would show "inchangé" to the validator and nothing in the banner — the user would think they
  /// changed something when they did not. Detect it at pick time: keep the current selection untouched and
  /// say so. Only meaningful when there IS a current file (a creation has nothing to compare against).
  private acceptFileIfDifferent(kind: 'excel' | 'pdf', file: File, accept: () => void): void {
    const industryId = this.currentItem?.resourceId;
    if (!industryId || !this.hasEnergyData) {
      accept();
      return;
    }

    this.industrieService.getCustomFile(industryId, kind).subscribe({
      next: async (currentFile: Blob) => {
        if (await this.sameContent(file, currentFile)) {
          this.dialog.open(DialogInfoComponent, {
            data: {
              title: this.translate.instant('industrie_fdes_file_identical_title'),
              message: this.translate.instant('industrie_fdes_file_identical')
            }
          });
          return; // selection untouched: the file already attached (server's) stays
        }
        accept();
      },
      // can't compare (file missing/offline) -> don't stand in the user's way, the backend still flags it
      error: () => accept()
    });
  }

  private async sameContent(file: File, other: Blob): Promise<boolean> {
    if (file.size !== other.size) return false;
    const [a, b] = await Promise.all([file.arrayBuffer(), other.arrayBuffer()]);
    const viewA = new Uint8Array(a);
    const viewB = new Uint8Array(b);
    return viewA.every((byte, i) => byte === viewB[i]);
  }

  downloadXls() {
    this.downloadCustomFile('excel', this.xlsFileName || this.energyProfile?.excelName || '');
  }

  downloadPdf() {
    this.downloadCustomFile('pdf', this.pdfFileName || this.energyProfile?.pdfName || '');
  }

  private downloadCustomFile(kind: 'excel' | 'pdf', name: string) {
    const id = this.currentItem?.resourceId;
    if (!id) return;
    this.industrieService.getCustomFile(id, kind).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name || (kind === 'excel' ? 'fiche.xlsx' : 'justificatif.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.snackbarService.open(this.translate.instant('snackbar_error'))
    });
  }

  // edit a combustible usage % (front)
  editCombustibleValue(element: any) {
    if (this.plantLocked) return;
    const ref = this.dialog.open(DialogEditCombustibleFormulaComponent, { data: { value: element.value } });
    ref.afterClosed().subscribe((value) => {
      if (value !== undefined && value !== null && value >= 0) {
        element.value = Number(value);
        if (this.tableCombustibles) this.tableCombustibles.renderRows();
      }
    });
  }

  // default plant -> reveal the custom deposit zones
  startCustomize() {
    this.customizing = true;
  }

  // unlock an existing custom plant for editing before resubmitting
  startEdit() {
    this.editingPlant = true;
    // show the current justification files as already-attached (names from the server), with a download
    // icon. They carry no File object → a new request is only created if the user replaces a file (option 1).
    this.xlsFile = null;
    this.pdfFile = null;
    this.xlsFileName = this.energyProfile?.excelName || '';
    this.pdfFileName = this.energyProfile?.pdfName || '';
    this.xlsFromServer = !!this.xlsFileName;
    this.pdfFromServer = !!this.pdfFileName;
    this.uploadsError = false;
  }

  // cancel a pending request — DELETE api/Industries/custom/{pendingCustomIndustryValuesId}
  cancelPlantRequest() {
    const requestId = this.currentItem?.pendingCustomIndustryValuesId;
    if (!requestId) return;
    this.industrieService.cancelCustomIndustryRequest(requestId).subscribe(() => {
      this.plantStatus = 'validated';
      this.currentItem.customStatus = 'validated';
      this.currentItem.pendingCustomIndustryValuesId = undefined;
      // Demande annulée : l'usine repasse « personnalisée ». Rafraîchir l'arborescence pour retirer la
      // catégorie « En attente de validation » (si plus aucune usine en attente) sans recharger la page.
      this.resourceListRefreshService.triggerRefresh();
    });
  }

  // admin (SEVE-TP Usines) action — force a validated custom plant back to the default fiche. The backend
  // recomputes the energy data (keeps the entered fuel mix, restores default elec / other energy, parc à
  // liant -> gaz); the front just triggers it and reloads so the plant redisplays as "par défaut".
  forceDefaultPlant() {
    const resourceId = this.currentItem?.resourceId;
    if (!resourceId) return;
    const ref = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('industrie_fdes_confirm_force_default') }
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.industrieService.forceDefaultIndustry(resourceId).subscribe({
        next: () => {
          this.loadIndustrie(resourceId);
          this.majStore(SnackbarTypes.update);
          // L'usine repasse « par défaut » : majStore(update) n'actualise pas l'arborescence (son garde),
          // on force le refresh pour qu'elle change de catégorie sans recharger la page.
          this.resourceListRefreshService.triggerRefresh();
        },
        error: () => this.snackbarService.open(this.translate.instant('snackbar_error'))
      });
    });
  }

  // amène le total du mix (et son message d'erreur) à l'écran après une sauvegarde refusée
  private scrollToCombustibleTotal(): void {
    this.combustibleTotalRowRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
